import { Platform, AlertController, LoadingController } from 'ionic-angular';
import { Capacitor, Plugins } from '@capacitor/core';
import { Injectable } from '@angular/core';
import { EventEmitter } from '../../includes/eventEmitter';
import { CrudApiProvider } from '../crud-api/crud-api';
import { EnvVariables } from '../../environments/environment';
import { UserProvider } from '../user/user';
import * as debug from 'debug';
import * as _ from 'lodash';
import { Pro } from '@ionic/pro';
import { AuthProvider } from '../auth/auth';
import { NativeAccessProvider } from '../native-access/native-access';
import { PushNotifications } from '@capacitor/push-notifications';
import { ToolsProvider } from '../tools/tools';
import { AndroidPermissions } from '@ionic-native/android-permissions';
import { ConfigProvider } from '../config/config';

let ready = false;

const savedTokens = {};

document.addEventListener('deviceready', () => {
  ready = true;
});

@Injectable()
export class PushNotificationsProvider extends EventEmitter {
  log = debug('app:PushNotificationsProvider');

  notifications = [];
  announcements = [];
  token: any;
  subscribed;
  listenersSet;
  hasSetToken = false;

  constructor(
    public crudApi: CrudApiProvider,
    public env: EnvVariables,
    public user: UserProvider,
    public auth: AuthProvider,
    public platform: Platform,
    public alertCtrl: AlertController,
    public nativeAccess: NativeAccessProvider,
    public tools: ToolsProvider,
    public androidPermissions: AndroidPermissions,
    public configs: ConfigProvider,
    public loadingCtrl: LoadingController
  ) {
    super();

    // Now initialized on device ready in app.ts
    // this.init();

    this.auth.isAuthenticated().then(
      authed => {
        this.loadNotifications();
      },
      e => {}
    );

    // Makes sure this device is registered on auth
    this.auth.on('authenticated', async () => {
      this.loadNotifications();
      this.initSubscribe();
      this.saveToken();
    });

    // remove when logged out
    this.auth.on('logout', () => {
      this.notifications = [];
      this.announcements = [];
      this.emit('update');
    });

    if (localStorage.getItem('pushHasOpened')) {
      this.log('not first load into the app');
      setTimeout(async () => {
        this.initSubscribe();
      }, 30e3);
    }

    document.addEventListener('resume', () => {
      this.loadNotifications();
    });

    setInterval(() => {
      if (document.hasFocus()) {
        this.loadNotifications();
      }
    }, 30e3);
  }

  getCount() {
    return _.size(this.notifications) + _.size(this.announcements);
  }

  markViewed(notification) {
    this.notifications = _.filter(this.notifications, n => {
      return n._id != notification._id;
    });

    this.emit('update');

    if (notification.viewed) {
      // don't double dip
      return;
    }

    this.crudApi
      .put(`/api/notifications/${notification._id}`, {
        viewed: true,
      })
      .then(
        () => {},
        e => {}
      );
  }

  async init() {
    this.log('initing push');
    this.initListeners();

    // Makes sure this device is registered
    // this.auth.isAuthenticated().then(
    //   authed => {
    //   },
    //   e => {}
    // );
  }

  async checkPerms() {
    const { hasPermission } = await this.androidPermissions.checkPermission(
      'android.permission.POST_NOTIFICATIONS'
    );
    this.log('hasPerms', hasPermission);
    if (!hasPermission) {
      await this.androidPermissions.requestPermission(
        'android.permission.POST_NOTIFICATIONS'
      );
    }
  }

  async initSubscribe() {
    // we have this function cause we don't want to ask the user
    // if we can send them push notifications unless they've logged in

    if (this.subscribed) {
      return;
    }

    this.loadNotifications();

    this.subscribed = true;

    try {
      if (!window['cordova']) {
        return (this.hasSetToken = true);
      }

      await new Promise(resolve => setTimeout(resolve, 3000));
      PushNotifications.requestPermissions().then(
        async result => {
          if (result.receive === 'granted') {
            // Register with Apple / Google to receive push via APNS/FCM
            this.log('request granted');
            this.tools.logAnalyticsEvent(
              'trackCustom',
              'push-notifications-request-granted'
            );
            if (this.platform.is('android')) {
              await this.checkPerms();
            }
            PushNotifications.register();
          } else {
            // Show some error
          }
        },
        e => {
          // isn't workin'
          this.hasSetToken = true;
        }
      );

      // On success, we should be able to receive notifications
      PushNotifications.addListener('registration', data => {
        const token = _.get(data, 'value');
        this.log('reg success', token);
        this.token = token;
        this.auth.pushToken = _.get(this.token, token);
        this.saveToken();
      });

      // Some issue with our setup and push will not work
      PushNotifications.addListener('registrationError', (error: any) => {
        this.hasSetToken = true;
        this.log('reg error', error);
      });

      setTimeout(() => {
        this.hasSetToken = true; // not really, but tried
      }, 20e3);
    } catch (e) {
      this.hasSetToken = true; // not really, but tried
    }
  }

  async initListeners() {
    if (this.listenersSet) {
      return;
    }

    this.loadNotifications();

    this.listenersSet = true;

    if (!window['cordova']) {
      return;
    }

    const onNotification = notification => {
      this.log('Push received: ', notification);

      let data = this.getNotificationData(notification);

      if (data) {
        this.emit('received', data);

        if (!_.get(notification, 'foreground')) {
          this.emit('open', data);
        } else {
          const type = _.get(data, 'type');

          if (type === 'BADGE') {
            this.emit('open', data);
          } else if (
            _.includes(['TRIP_COMPLETE', 'NEAR_PARKING', 'WEIGH_STATION'], type)
          ) {
            let al = this.alertCtrl.create({
              title: data.title,
              message: data.message || data.body,
              buttons: [
                {
                  text: 'Ignore',
                  role: 'cancel',
                  handler: () => {
                    this.log('Cancel clicked');
                  },
                },
                {
                  text: 'Continue',
                  handler: () => {
                    this.emit('open', data);
                  },
                },
              ],
            });
            al.present();
          }
        }
      }

      this.loadNotifications();
    };

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', notification => {
      _.set(notification, 'foreground', true);
      this.log('pushNotificationReceived', notification);
      const data = this.getNotificationData(notification);

      this.tools.logAnalyticsEvent(
        'trackCustom',
        'push-notifications-received',
        {
          utms: _.get(data, 'metadata.utms'),
        }
      );

      new Promise(resolve => setTimeout(resolve, 100));
      onNotification(notification);
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', d => {
      const loading = this.loadingCtrl.create(
        this.tools.loadingMessageIfAdmin('action performed ...')
      );
      loading.present();

      const notification = _.get(d, 'notification', {});
      _.set(notification, 'foreground', false);
      this.log('pushNotificationActionPerformed', notification);

      const data = this.getNotificationData(notification);

      this.tools.logAnalyticsEvent(
        'trackCustom',
        'push-notifications-action-performed',
        {
          utms: _.get(data, 'metadata.utms'),
        }
      );
      try {
        loading.dismiss();
        loading.dismissAll();
      } catch (e) {}
      new Promise(resolve => setTimeout(resolve, 100));
      onNotification(notification);
    });

    // alert('listeners set');
    try {
      this.createChannels();
    } catch (e) {
      // alert('channels failed');
    }
  }

  getNotificationData(notification: any) {
    try {
      let data =
        _.get(notification, 'data.additionalData') || // android
        _.get(notification, 'data.payload.data') ||
        _.get(notification, 'payload.data') ||
        _.get(notification, 'data.aps.payload.data'); // ios

      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {}
      }

      if (_.get(data, 'data')) {
        // we nested it for some reason
        data = _.get(data, 'data');
      }
      return data;
    } catch (e) {
      return {};
    }
  }

  loadNotifications() {
    if (!this.user.get('_id')) return;
    this.crudApi
      .get('/api/notifications', {
        fields: '_id',
        viewed: false,
      })
      .then(
        res => {
          this.notifications = res.data;
          this.emit('update');

          try {
            const pg = _.get(window, 'cordova.plugins.notification');
            if (_.size(res.data)) {
              pg.badge.set(_.size(res.data));
            } else {
              pg.badge.clear();
            }
          } catch (e) {}
        },
        e => {
          this.notifications = [];
          this.emit('update');
        }
      );

    this.crudApi
      .get('/api/announcements', { limit: 100, announcementType: 'minor' })
      .then(
        res => {
          let tmpAnnouncements = _.clone(res.data);
          this.emit('update');

          let viewed = this.user.get('announcements') || [];

          tmpAnnouncements = _.filter(tmpAnnouncements, a => {
            return !_.includes(viewed, a._id);
          });

          this.announcements = tmpAnnouncements;
        },
        e => {
          this.announcements = [];
          this.emit('update');
        }
      );
  }

  private saveToken() {
    const token = this.token,
      api = this.platform.is('android') ? 'gcm' : 'apns';

    if (!token) {
      return;
    }

    this.log('token', this.token);

    if (savedTokens[token + this.user.get('_id')]) {
      return;
    }
    savedTokens[token + this.user.get('_id')] = true;

    return this.crudApi
      .post(`/api/users/${this.user.get('_id')}/deviceTokens`, {
        token: token,
        api: api,
      })
      .then(
        () => {
          this.hasSetToken = true;
          this.log('added token');
        },
        e => {
          this.hasSetToken = true;
          this.log('failed to add token', e);
        }
      );
  }

  removeAnnouncement(announcement) {
    this.announcements = _.filter(this.announcements, a => {
      return a._id !== announcement._id;
    });
    this.emit('update');
  }

  async createChannels() {
    if (Capacitor.getPlatform() !== 'android') return;
    const configChannels = this.configs.get('notification-channels-android');
    const defaults = this.configs.get(
      'notification-channels-android--defaults'
    ) || ['fcm_fallback_notification_channel', 'default'];
    const current = await PushNotifications.listChannels();
    const currentNoneDefaultChannels = _.filter(
      current.channels,
      ch => !_.includes(defaults, ch.id)
    );

    _.each(configChannels, c => {
      if (!_.find(currentNoneDefaultChannels, curr => curr.id === c.id)) {
        PushNotifications.createChannel(c);
      }
    });

    // commenting out for samsung devices crashing (todo)
    // _.each(currentNoneDefaultChannels, curr => {
    //   if (!_.find(configChannels, c => c.id === curr.id)) {
    //     try {
    //       PushNotifications.deleteChannel({ id: curr.id });
    //     } catch (e) {}
    //   }
    // });

    const newCurrents = await PushNotifications.listChannels();
    const mapped = _.map(newCurrents.channels, c => c.id);
    this.log('current notif channels', mapped);
  }
}



// WEBPACK FOOTER //
// ./src/providers/push-notifications/push-notifications.ts